import type { LogData, LoggerStorageRetryOptions, StoredLogRecord } from './types'

const DB_NAME = 'seven-client-retail-logger'
const DB_VERSION = 1
const LOG_STORE_NAME = 'logs'
const DEFAULT_RETRY_BASE_DELAY = 60_000
const DEFAULT_RETRY_MAX_DELAY = 15 * 60_000

let dbPromise: Promise<IDBDatabase> | undefined

const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> => {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

const waitForTransaction = (transaction: IDBTransaction): Promise<void> => {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
    transaction.onabort = () => reject(transaction.error)
  })
}

const openDb = (): Promise<IDBDatabase> => {
  if (dbPromise) {
    return dbPromise
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(LOG_STORE_NAME)) {
        const store = db.createObjectStore(LOG_STORE_NAME, { keyPath: 'id' })
        store.createIndex('createdAt', 'createdAt')
        store.createIndex('nextRetryAt', 'nextRetryAt')
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

  return dbPromise
}

export const createStoredLogRecord = (logData: LogData): StoredLogRecord => {
  const now = Date.now()
  return {
    id: crypto.randomUUID(),
    log: logData,
    createdAt: now,
    updatedAt: now,
    attempts: 0,
    nextRetryAt: 0
  }
}

export const getAllRecords = async (): Promise<Array<StoredLogRecord>> => {
  const db = await openDb()
  const transaction = db.transaction(LOG_STORE_NAME, 'readonly')
  const store = transaction.objectStore(LOG_STORE_NAME)
  const records = await requestToPromise<Array<StoredLogRecord>>(store.getAll())
  await waitForTransaction(transaction)
  return records
}

const putRecords = async (records: Array<StoredLogRecord>): Promise<void> => {
  if (records.length === 0) {
    return
  }

  const db = await openDb()
  const transaction = db.transaction(LOG_STORE_NAME, 'readwrite')
  const store = transaction.objectStore(LOG_STORE_NAME)
  records.forEach(record => store.put(record))
  await waitForTransaction(transaction)
}

export const deleteRecords = async (ids: Array<string>): Promise<void> => {
  if (ids.length === 0) {
    return
  }

  const db = await openDb()
  const transaction = db.transaction(LOG_STORE_NAME, 'readwrite')
  const store = transaction.objectStore(LOG_STORE_NAME)
  ids.forEach(id => store.delete(id))
  await waitForTransaction(transaction)
}

export const clearLogRecords = async (): Promise<void> => {
  const db = await openDb()
  const transaction = db.transaction(LOG_STORE_NAME, 'readwrite')
  transaction.objectStore(LOG_STORE_NAME).clear()
  await waitForTransaction(transaction)
}

const getEvictionCandidates = (records: Array<StoredLogRecord>, overLimit: number): Array<StoredLogRecord> => {
  const sortedRecords = [...records].sort((first, second) => first.createdAt - second.createdAt)
  const lowLevelRecords = sortedRecords.filter(record => ['trace', 'debug'].includes(String(record.log.level)))
  const selectedRecords = new Map<string, StoredLogRecord>()

  lowLevelRecords.slice(0, overLimit).forEach(record => selectedRecords.set(record.id, record))

  if (selectedRecords.size < overLimit) {
    sortedRecords.forEach(record => {
      if (selectedRecords.size < overLimit) {
        selectedRecords.set(record.id, record)
      }
    })
  }

  return Array.from(selectedRecords.values())
}

const enforceBacklogLimit = async (maxStoredLogs: number): Promise<void> => {
  const records = await getAllRecords()
  const overLimit = records.length - maxStoredLogs

  if (overLimit <= 0) {
    return
  }

  const recordsToDrop = getEvictionCandidates(records, overLimit)
  await deleteRecords(recordsToDrop.map(record => record.id))
  console.warn('[loggerHttp] Log backlog exceeded storage limit. Dropped oldest logs.', {
    dropped: recordsToDrop.length,
    droppedLevels: recordsToDrop.map(record => record.log.level)
  })
}

export const addLogRecord = async (record: StoredLogRecord, maxStoredLogs: number): Promise<void> => {
  const db = await openDb()
  const transaction = db.transaction(LOG_STORE_NAME, 'readwrite')
  transaction.objectStore(LOG_STORE_NAME).add(record)
  await waitForTransaction(transaction)
  await enforceBacklogLimit(maxStoredLogs)
}

export const getSendableBatch = async (batchSize: number): Promise<Array<StoredLogRecord>> => {
  const records = await getAllRecords()
  const now = Date.now()
  return records
    .filter(record => record.nextRetryAt <= now)
    .sort((first, second) => first.createdAt - second.createdAt)
    .slice(0, batchSize)
}

const getRetryDelay = (attempts: number, options: LoggerStorageRetryOptions): number => {
  const baseDelay = options.retryBaseDelay || DEFAULT_RETRY_BASE_DELAY
  const maxDelay = options.retryMaxDelay || DEFAULT_RETRY_MAX_DELAY
  const exponentialDelay = Math.min(baseDelay * 2 ** Math.max(attempts - 1, 0), maxDelay)
  // Jitter helps avoid all clients retrying at the exact same time.
  // Imagine many terminals lose connection, then recover together.
  // Without jitter, they all retry at exactly 1 minute, then 2 minutes, then 4 minutes.
  // With jitter, retries spread out a little.
  const jitter = Math.round(exponentialDelay * Math.random() * 0.2)
  return exponentialDelay + jitter
}

export const restoreFailedRecords = async (
  records: Array<StoredLogRecord>,
  retryOptions: LoggerStorageRetryOptions
): Promise<void> => {
  const now = Date.now()
  const failedRecords = records.map(record => {
    const attempts = record.attempts + 1
    return {
      ...record,
      attempts,
      nextRetryAt: now + getRetryDelay(attempts, retryOptions),
      updatedAt: now
    }
  })

  await putRecords(failedRecords)
}
